/**
 * CR283: Merchant Directory Table Component
 * CR309: Added scan health warnings (WEB/FLYER source counts)
 * CR314: Added category icon fallback for merchant logos
 */

import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  MoreHorizontal,
  Eye,
  CheckCircle,
  EyeOff,
  RefreshCw,
  ChevronDown,
  AlertTriangle,
  Search,
  Plus,
  Globe,
  FileText,
  RotateCcw,
  Trash2,
} from "lucide-react";
import {
  type MerchantDirectoryRow,
  useMerchantDirectoryMutations,
} from "@/hooks/useMerchantsDirectory";
import { useMerchantsScanHealth, type MerchantScanHealth } from "@/hooks/useMerchantsScanHealth";
import { useCategoryIconMap, getCategoryIconFromMap } from "@/hooks/useCategoryIconMap";
import { MerchantMark } from "./MerchantMark";

interface MerchantDirectoryTableProps {
  merchants: MerchantDirectoryRow[];
  loading: boolean;
  status: "pending" | "active_scan" | "active_noscan" | "hidden" | "deleted";
  /** CR314: Whether to show category icons as fallback for missing logos */
  categoryIconFallbackEnabled: boolean;
  /** CR314: Callback to toggle fallback setting */
  onToggleCategoryIconFallback: (enabled: boolean) => void;
}

export function MerchantDirectoryTable({
  merchants,
  loading,
  status,
  categoryIconFallbackEnabled,
  onToggleCategoryIconFallback,
}: MerchantDirectoryTableProps) {
  const isActiveStatus = status === "active_scan" || status === "active_noscan";
  const statusLabel =
    status === "active_scan"
      ? "Active Scan"
      : status === "active_noscan"
      ? "Active No Scan"
      : status === "pending"
      ? "Pending"
      : status === "deleted"
      ? "Deleted"
      : "Hidden";

  const [search, setSearch] = useState("");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [eliminateModalOpen, setEliminateModalOpen] = useState(false);
  const [merchantToEliminate, setMerchantToEliminate] = useState<MerchantDirectoryRow | null>(null);
  const [eliminateConfirmText, setEliminateConfirmText] = useState("");

  // CR314: Load category icons for fallback
  const { data: categoryIconMap } = useCategoryIconMap();

  // CR309: Fetch scan health data for all merchants
  const { data: scanHealthMap } = useMerchantsScanHealth();

  const {
    setStatus,
    isSettingStatus,
    setScanEnabled,
    isSettingScan,
    resyncLegacy,
    isResyncing,
    softDelete,
    isSoftDeleting,
    restoreMerchant,
    isRestoringMerchant,
    eliminateMerchant,
    isEliminatingMerchant,
  } = useMerchantDirectoryMutations();

  const filteredMerchants = merchants.filter(
    (m) =>
      m.display_name.toLowerCase().includes(search.toLowerCase()) ||
      m.merchant_key.toLowerCase().includes(search.toLowerCase())
  );

  // CR309: Helper to get scan health for a merchant
  const getScanHealth = (merchantKey: string): MerchantScanHealth | undefined => {
    return scanHealthMap?.get(merchantKey);
  };

  const toggleExpanded = (key: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const openEliminateModal = (merchant: MerchantDirectoryRow) => {
    setMerchantToEliminate(merchant);
    setEliminateConfirmText("");
    setEliminateModalOpen(true);
  };

  const closeEliminateModal = () => {
    setEliminateModalOpen(false);
    setMerchantToEliminate(null);
    setEliminateConfirmText("");
  };

  const handleConfirmEliminate = async () => {
    if (!merchantToEliminate) return;
    await eliminateMerchant({ merchantKey: merchantToEliminate.merchant_key });
    closeEliminateModal();
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{statusLabel} Merchants</CardTitle>
            <CardDescription>
              {filteredMerchants.length} merchant{filteredMerchants.length !== 1 ? "s" : ""}
            </CardDescription>
            {status === "deleted" && (
              <CardDescription className="mt-1">
                Soft deleted merchants are kept for reference.
              </CardDescription>
            )}
          </div>
          <div className="flex items-center gap-4">
            {/* CR314: Category icon fallback toggle */}
            <div className="flex items-center gap-2">
              <Switch
                id="category-fallback"
                checked={categoryIconFallbackEnabled}
                onCheckedChange={onToggleCategoryIconFallback}
              />
              <Label htmlFor="category-fallback" className="text-sm text-muted-foreground cursor-pointer">
                Category icon fallback
              </Label>
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search merchants..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 w-64"
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <TableHead>Logo</TableHead>
              <TableHead>Display Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Scan</TableHead>
              <TableHead>Sources</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredMerchants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                  No merchants found
                </TableCell>
              </TableRow>
            ) : (
              filteredMerchants.map((merchant) => {
                const isExpanded = expandedRows.has(merchant.merchant_key);
                const hasMismatch = merchant.scan_enabled_mismatch || merchant.hidden_mismatch;
                // CR309: Get scan health for this merchant
                const scanHealth = getScanHealth(merchant.merchant_key);
                const hasWebWarning = scanHealth?.has_web_sources_warning ?? false;
                const hasFlyerWarning = scanHealth?.has_flyer_sources_warning ?? false;
                const hasScanWarning = hasWebWarning || hasFlyerWarning;

                return (
                  <Collapsible key={merchant.merchant_key} asChild open={isExpanded}>
                    <>
                      <TableRow className={hasMismatch ? "bg-amber-50 dark:bg-amber-950/20" : undefined}>
                        <TableCell>
                          <CollapsibleTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => toggleExpanded(merchant.merchant_key)}
                            >
                              <ChevronDown
                                className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                              />
                            </Button>
                          </CollapsibleTrigger>
                        </TableCell>
                        {/* CR314: Use MerchantMark with category icon fallback */}
                        <TableCell>
                          <MerchantMark
                            displayName={merchant.display_name}
                            logoSvg={merchant.logo_svg}
                            logoUrl={merchant.logo_url}
                            categoryIcon={getCategoryIconFromMap(categoryIconMap, merchant.category)}
                            fallbackEnabled={categoryIconFallbackEnabled}
                          />
                        </TableCell>
                        <TableCell>
                          <Link
                            to={`/admin/merchants/${merchant.merchant_key}`}
                            className="font-medium hover:underline"
                          >
                            {merchant.display_name}
                          </Link>
                          <div className="text-xs text-muted-foreground font-mono">
                            {merchant.merchant_key}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{merchant.category}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{merchant.merchant_type}</Badge>
                        </TableCell>
                        <TableCell>
                          {status !== "deleted" && merchant.sources_count > 0 ? (
                            <Switch
                              checked={merchant.scan_enabled}
                              onCheckedChange={(enabled) =>
                                setScanEnabled({ merchantKey: merchant.merchant_key, enabled })
                              }
                              disabled={isSettingScan}
                            />
                          ) : status === "deleted" ? (
                            <span className="text-xs text-muted-foreground">—</span>
                          ) : (
                            <Button variant="ghost" size="sm" asChild className="text-xs">
                              <Link to={`/admin/merchant-sources?merchant_key=${merchant.merchant_key}`}>
                                <Plus className="h-3 w-3 mr-1" />
                                Add
                              </Link>
                            </Button>
                          )}
                        </TableCell>
                        {/* CR309: Sources column with WEB/FLYER counts and warnings */}
                        <TableCell>
                          <TooltipProvider>
                            <div className="flex items-center gap-2">
                              {/* WEB sources */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className={`flex items-center gap-0.5 ${hasWebWarning ? 'text-warning' : 'text-muted-foreground'}`}>
                                    <Globe className="h-3 w-3" />
                                    <span className="text-xs tabular-nums">
                                      {scanHealth?.active_web_sources_count ?? 0}
                                    </span>
                                    {hasWebWarning && <AlertTriangle className="h-3 w-3" />}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>
                                    {scanHealth?.active_web_sources_count ?? 0} active WEB source(s)
                                    {hasWebWarning && " — Scan enabled but no WEB sources"}
                                  </p>
                                </TooltipContent>
                              </Tooltip>

                              {/* FLYER sources */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className={`flex items-center gap-0.5 ${hasFlyerWarning ? 'text-warning' : 'text-muted-foreground'}`}>
                                    <FileText className="h-3 w-3" />
                                    <span className="text-xs tabular-nums">
                                      {scanHealth?.active_flyer_sources_count ?? 0}
                                    </span>
                                    {hasFlyerWarning && <AlertTriangle className="h-3 w-3" />}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>
                                    {scanHealth?.active_flyer_sources_count ?? 0} active FLYER source(s)
                                    {hasFlyerWarning && " — Scan enabled but no FLYER sources"}
                                  </p>
                                </TooltipContent>
                              </Tooltip>

                              {/* Total count */}
                              <span className="text-xs text-muted-foreground tabular-nums ml-1">
                                ({merchant.active_sources_count}/{merchant.sources_count})
                              </span>
                            </div>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {format(new Date(merchant.updated_at), "MMM d, yyyy")}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link to={`/admin/merchants/${merchant.merchant_key}`}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  View Details
                                </Link>
                              </DropdownMenuItem>
                              {/* CR313: View Sources deep-link (using merchant_key; UUID support ready when view is updated) */}
                              <DropdownMenuItem asChild>
                                <Link to={`/admin/merchant-sources?merchant_key=${merchant.merchant_key}`}>
                                  <Globe className="mr-2 h-4 w-4" />
                                  View Sources
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {status === "pending" && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    setStatus({ merchantKey: merchant.merchant_key, action: "activate" })
                                  }
                                  disabled={isSettingStatus}
                                >
                                  <CheckCircle className="mr-2 h-4 w-4" />
                                  Activate
                                </DropdownMenuItem>
                              )}
                              {isActiveStatus && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    setStatus({ merchantKey: merchant.merchant_key, action: "hide" })
                                  }
                                  disabled={isSettingStatus}
                                >
                                  <EyeOff className="mr-2 h-4 w-4" />
                                  Hide
                                </DropdownMenuItem>
                              )}
                              {status === "hidden" && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    setStatus({ merchantKey: merchant.merchant_key, action: "unhide" })
                                  }
                                  disabled={isSettingStatus}
                                >
                                  <Eye className="mr-2 h-4 w-4" />
                                  Unhide
                                </DropdownMenuItem>
                              )}
                              {status !== "deleted" && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => softDelete({ merchantKey: merchant.merchant_key })}
                                    disabled={isSoftDeleting}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                              {status === "deleted" && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => restoreMerchant({ merchantKey: merchant.merchant_key })}
                                    disabled={isRestoringMerchant}
                                  >
                                    <RotateCcw className="mr-2 h-4 w-4" />
                                    Restore
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => openEliminateModal(merchant)}
                                    disabled={isEliminatingMerchant}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Eliminate…
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>

                      {/* Expanded diagnostics row */}
                      <CollapsibleContent asChild>
                        <TableRow className="bg-muted/30">
                          <TableCell colSpan={9}>
                            <div className="p-4 space-y-4">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <div className="text-muted-foreground">Computed Scan</div>
                                  <div className="font-medium">{merchant.scan_enabled ? "Yes" : "No"}</div>
                                </div>
                                <div>
                                  <div className="text-muted-foreground">Sources</div>
                                  <div className="font-medium">
                                    {merchant.active_sources_count} active / {merchant.sources_count} total
                                  </div>
                                </div>
                                <div>
                                  <div className="text-muted-foreground">Legacy scan_enabled</div>
                        <div className="font-medium flex items-center gap-2">
                            {merchant.legacy_scan_enabled ? "true" : "false"}
                            {merchant.scan_enabled_mismatch && (
                              <AlertTriangle className="h-4 w-4 text-warning" />
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-muted-foreground">Legacy is_hidden</div>
                        <div className="font-medium flex items-center gap-2">
                            {merchant.legacy_is_hidden ? "true" : "false"}
                            {merchant.hidden_mismatch && (
                              <AlertTriangle className="h-4 w-4 text-warning" />
                                    )}
                                  </div>
                                </div>
                              </div>

                              {hasMismatch && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => resyncLegacy(merchant.merchant_key)}
                                  disabled={isResyncing}
                                >
                                  <RefreshCw className={`mr-2 h-4 w-4 ${isResyncing ? "animate-spin" : ""}`} />
                                  Resync Legacy Flags
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      </CollapsibleContent>
                    </>
                  </Collapsible>
                );
              })
            )}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={eliminateModalOpen} onOpenChange={(next) => !next ? closeEliminateModal() : setEliminateModalOpen(next)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminate merchant permanently?</DialogTitle>
            <DialogDescription>
              This action permanently deletes this merchant record. This cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <p className="text-sm">
              Are you sure you want to eliminate{" "}
              <span className="font-medium">{merchantToEliminate?.display_name ?? "this merchant"}</span>?
            </p>
            <div className="space-y-1">
              <Label htmlFor="eliminate-confirm-input" className="text-xs text-muted-foreground">
                Type <span className="font-mono font-semibold">ELIMINATE</span> to enable the final action
              </Label>
              <Input
                id="eliminate-confirm-input"
                value={eliminateConfirmText}
                onChange={(e) => setEliminateConfirmText(e.target.value)}
                placeholder="ELIMINATE"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeEliminateModal}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmEliminate}
              disabled={eliminateConfirmText.trim().toUpperCase() !== "ELIMINATE" || isEliminatingMerchant}
            >
              Eliminate permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
